import { captureException } from '@sentry/vue'
import type { ApolloQueryResult } from '@apollo/client'
import type { FavoriteConnection, FavoriteEdge } from '@onxmaps/supergraph'
import { setFeaturesFavorited } from '@/services/map-service'
import type MapFeature from '@/lib/map-feature'
import type {
  Args,
  FavoriteQueryResult,
} from '@/fish/lib/highlight-favorites/types'

/**
 * Highlight favorited content on the map
 * Requests all items of a FavoritableType, and applies the `favorited: true` state to map features
 * Map features MUST have a feature ID that matches their KG ID
 */
const highlightFavoritesOnMap = async (args: Args) => {
  const { source, sourceLayer, getFavoritesFn } = args
  const accumulatedEdges: FavoriteEdge[] = []
  const { result, fetchMore, refetch } = getFavoritesFn()
  // Wait for a fresh network response so any recent mutations are included
  try {
    await refetch()
  } catch (e) {
    captureException(e, {
      tags: {
        context: 'favorite-highlighting',
      },
    })
  }

  const initialConnection = result.value?.favoriteConnection
  accumulatedEdges.push(...(initialConnection?.edges ?? []))

  let hasNextPage = initialConnection?.pageInfo?.hasNextPage ?? false

  let after: string | null | undefined =
    initialConnection?.pageInfo?.endCursor ?? null
  while (hasNextPage && after !== null) {
    const fetchMoreResult: ApolloQueryResult<FavoriteQueryResult> | undefined =
      await fetchMore({
        variables: { after: after },
      })
    const connection: FavoriteConnection | null | undefined =
      fetchMoreResult?.data?.favoriteConnection
    accumulatedEdges.push(...(connection?.edges ?? []))

    hasNextPage = connection?.pageInfo?.hasNextPage ?? false
    after = connection?.pageInfo?.endCursor ?? null
  }

  if (accumulatedEdges.length) {
    const featuresToSet = accumulatedEdges.reduce<MapFeature[]>((acc, item) => {
      const id = item.node?.entity.id
      if (id !== null && id !== undefined) {
        acc.push({
          id,
          source,
          sourceLayer,
        } as MapFeature)
      }
      return acc
    }, [])
    setFeaturesFavorited(featuresToSet)
  } else {
    setFeaturesFavorited([])
  }
}

export default highlightFavoritesOnMap
